import React, { useCallback, useMemo, useRef, useState } from 'react';

// ── Generic client-side table sorting ────────────────────────────────────────
// A single reusable mechanism so EVERY dashboard table (Deployment sub-views,
// Attendance, Red Flag, Mood, Appeals) sorts identically. It mirrors the bespoke
// Store Performance sort exactly:
//   • clicking a column header cycles  desc → asc → clear (back to default order).
//     A table can start chosen columns ascending instead (`firstDir`) — a roster's
//     text columns read A→Z first — and the cycle is then  asc → desc → clear.
//   • missing values (null / undefined / '') always sink to the bottom, both dirs
//   • numbers compare numerically; everything else via locale-aware `numeric`
//     compare (so "L2" < "L10", "10" < "2" handled sensibly).
// When no column is active the ORIGINAL row order (server / pre-sorted) is kept.

export type SortDir = 'asc' | 'desc';
// A column's comparable value. null / undefined / '' → always sorts to the bottom.
export type SortValue = number | string | null | undefined;

// Map of column key → how to pull that column's comparable value from a row.
export type SortAccessors<T> = Record<string, (row: T) => SortValue>;

export interface TableSort<T> {
  sorted: T[];
  sortKey: string | null;
  sortDir: SortDir;
  toggleSort: (key: string) => void;
}

const isMissing = (v: SortValue): boolean =>
  v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

// Text comparison for every sortable table: locale-aware with numeric runs ("L2" <
// "L10"), case-insensitive. One cached collator — String.localeCompare with options
// builds a new one per call, ~30× slower on a month of report rows (~35k).
const textCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
export const compareSortText = (a: string, b: string): number => textCollator.compare(a, b);

/** A date or timestamp as epoch ms for sorting — null when blank or unparseable, so the
 *  shared sort sinks it to the bottom instead of treating it as the oldest date. */
export const toEpochMs = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') return null;
  const ms = new Date(value as string | number).getTime();
  return Number.isNaN(ms) ? null : ms;
};

export interface SortOptions {
  defaultKey?: string;
  defaultDir?: SortDir;
  /** The direction a column takes on its FIRST click. Omitted → 'desc' for every
   *  column, which suits the numeric report tables. A roster passes 'asc' for its text
   *  columns so a first click reads A→Z. The second click is the opposite direction
   *  and the third clears, whichever way the column started. */
  firstDir?: (key: string) => SortDir;
}

// The sort STATE on its own — active column, direction and the click cycle — for a
// table whose rows are sorted somewhere else, e.g. a server-paginated report that
// sends sortKey / sortDir to its API. useTableSort below is this plus in-browser sorting.
export function useSortState(
  opts?: SortOptions,
): Pick<TableSort<unknown>, 'sortKey' | 'sortDir' | 'toggleSort'> {
  // Column and direction live in ONE state value, moved by a pure updater. They used
  // to be two states, with the direction's updater called from inside the column's and
  // a flag read back out of it to decide "cleared" — which only held while React ran
  // that inner updater eagerly. After the first sort click it no longer did, so the
  // third click flipped back to descending instead of clearing.
  const [sort, setSort] = useState<{ key: string | null; dir: SortDir }>({
    key: opts?.defaultKey ?? null,
    dir: opts?.defaultDir ?? 'desc',
  });
  // Read through a ref so toggleSort stays stable when a caller passes an inline function.
  const firstDirRef = useRef(opts?.firstDir);
  firstDirRef.current = opts?.firstDir;

  // first direction → the opposite → clear.
  const toggleSort = useCallback((key: string) => {
    const first = firstDirRef.current?.(key) ?? 'desc';
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: first };
      if (prev.dir === first) return { key, dir: first === 'desc' ? 'asc' : 'desc' };
      return { key: null, dir: 'desc' };
    });
  }, []);

  return { sortKey: sort.key, sortDir: sort.dir, toggleSort };
}

export function useTableSort<T>(
  rows: T[],
  accessors: SortAccessors<T>,
  opts?: SortOptions,
): TableSort<T> {
  const { sortKey, sortDir, toggleSort } = useSortState(opts);

  const sorted = useMemo(() => {
    const get = sortKey ? accessors[sortKey] : undefined;
    if (!get) return rows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      const na = isMissing(va);
      const nb = isMissing(vb);
      if (na && nb) return 0;
      if (na) return 1; // missing always sinks to the bottom (direction-independent)
      if (nb) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return compareSortText(String(va), String(vb)) * dir;
    });
    // accessors is expected to be an inline literal (new each render); these tables
    // are small so re-sorting per render is negligible and correctness-safe.
  }, [rows, sortKey, sortDir, accessors]);

  return { sorted, sortKey, sortDir, toggleSort };
}

// ── Sortable column header ────────────────────────────────────────────────────
// Drop-in replacement for a plain <th>. Pass the table's own <th> classes via
// `className` (padding/whitespace differ per table) — the component only adds the
// click affordance + the ↕ / ▲ / ▼ indicator.
export const SortTh: React.FC<{
  label: React.ReactNode;
  sortKey: string;
  sort: Pick<TableSort<unknown>, 'sortKey' | 'sortDir' | 'toggleSort'>;
  align?: 'left' | 'right' | 'center';
  className?: string;
  title?: string;
  /** Rendered beside the sort button, outside it — e.g. an info popover whose
   *  clicks must not also toggle the sort. */
  suffix?: React.ReactNode;
  /** For a header cell that spans several header rows (grouped column headers). */
  rowSpan?: number;
}> = ({ label, sortKey, sort, align = 'left', className = '', title, suffix, rowSpan }) => {
  const active = sort.sortKey === sortKey;
  const alignCls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  const justify =
    align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  const button = (
    <button
      type="button"
      onClick={() => sort.toggleSort(sortKey)}
      // Tailwind's preflight resets text-transform on buttons; inherit it so a header
      // row styled `uppercase` keeps its uppercase labels once they become sortable.
      style={{ textTransform: 'inherit' }}
      className={`inline-flex ${suffix ? '' : 'w-full '}items-center gap-1 hover:text-gray-700 ${justify}`}
    >
      {label}
      {/* Fixed width: ▲/▼ are wider than ↕, and a header-sized column would otherwise
          shift by a couple of pixels each time it became the sorted one. */}
      <span className={`inline-block w-3 text-center text-[10px] ${active ? 'text-primary-600' : 'text-gray-400'}`}>
        {active ? (sort.sortDir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </button>
  );
  return (
    <th className={`${alignCls} ${className}`} title={title} rowSpan={rowSpan}>
      {suffix ? (
        <span className={`inline-flex w-full items-center gap-1 ${justify}`}>
          {button}
          {suffix}
        </span>
      ) : button}
    </th>
  );
};
